import axios from 'axios';

// API基础URL - 开发环境使用本地服务器，生产环境使用Cloudflare Workers URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_API_URL || 'https://online-quiz-system.studytest123.workers.dev'
  : 'http://localhost:8787';

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token过期或无效，清除本地存储并重定向到登录页
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 类型定义
export interface User {
  id: number;
  email: string;
  username: string;
  role: 'student' | 'teacher' | 'admin';
  full_name?: string;
  avatar_url?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  full_name?: string;
  role?: 'student' | 'teacher';
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface Question {
  id: number;
  type: 'single_choice' | 'multiple_choice' | 'true_false' | 'fill_blank' | 'subjective';
  title: string;
  content: string;
  options?: string[];
  correct_answer: string;
  explanation?: string;
  difficulty: number;
  category_id?: number;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface PracticeSession {
  id: number;
  user_id: number;
  category_id?: number;
  difficulty?: number;
  total_questions: number;
  answered_questions: number;
  correct_answers: number;
  started_at: string;
  completed_at?: string;
  status: 'active' | 'completed' | 'abandoned';
}

export interface Exam {
  id: number;
  title: string;
  description?: string;
  category_id?: number;
  total_questions: number;
  time_limit?: number;
  max_attempts: number;
  is_published: boolean;
  start_time?: string;
  end_time?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

// 认证API
export const authAPI = {
  login: (data: LoginRequest): Promise<ApiResponse<{ token: string; user: User }>> =>
    api.post('/api/auth/login', data).then(res => res.data),
  
  register: (data: RegisterRequest): Promise<ApiResponse<{ token: string; user: User }>> =>
    api.post('/api/auth/register', data).then(res => res.data),
  
  getMe: (): Promise<ApiResponse<User>> =>
    api.get('/api/auth/me').then(res => res.data),
  
  updateProfile: (data: Partial<User>): Promise<ApiResponse<User>> =>
    api.put('/api/auth/me', data).then(res => res.data),
  
  changePassword: (data: { current_password: string; new_password: string }): Promise<ApiResponse> =>
    api.post('/api/auth/change-password', data).then(res => res.data),
};

// 题目API
export const questionsAPI = {
  getQuestions: (params?: {
    page?: number;
    limit?: number;
    category_id?: number;
    difficulty?: number;
    type?: string;
  }): Promise<ApiResponse<{ items: Question[]; total: number; page: number; limit: number; totalPages: number }>> =>
    api.get('/api/questions', { params }).then(res => res.data),
  
  getQuestion: (id: number): Promise<ApiResponse<Question>> =>
    api.get(`/api/questions/${id}`).then(res => res.data),
  
  createQuestion: (data: Omit<Question, 'id' | 'created_at' | 'updated_at' | 'created_by'> & { created_by?: number }): Promise<ApiResponse<{ id: number }>> =>
    api.post('/api/questions', data).then(res => res.data),
  
  updateQuestion: (id: number, data: Partial<Question>): Promise<ApiResponse> =>
    api.put(`/api/questions/${id}`, data).then(res => res.data),
  
  deleteQuestion: (id: number): Promise<ApiResponse> =>
    api.delete(`/api/questions/${id}`).then(res => res.data),
};

// 练习API
export const practiceAPI = {
  startPractice: (data: {
    category_id?: number;
    difficulty?: number;
    total_questions: number;
    question_types?: string[];
  }): Promise<ApiResponse<{
    session_id: number;
    total_questions: number;
    questions: Question[];
  }>> =>
    api.post('/api/practice/start', data).then(res => res.data),
  
  getSession: (id: number): Promise<ApiResponse<{ session: PracticeSession; answers: any[] }>> =>
    api.get(`/api/practice/session/${id}`).then(res => res.data),
  
  submitAnswer: (data: { question_id: number; user_answer?: string }): Promise<ApiResponse<{
    is_correct: boolean;
    points: number;
    correct_answer: string;
    explanation?: string;
    session_progress: any;
    completed: boolean;
  }>> =>
    api.post('/api/practice/submit-answer', data).then(res => res.data),
  
  completeSession: (id: number): Promise<ApiResponse> =>
    api.post(`/api/practice/complete/${id}`).then(res => res.data),
  
  abandonSession: (id: number): Promise<ApiResponse> =>
    api.post(`/api/practice/abandon/${id}`).then(res => res.data),
  
  getHistory: (params?: { page?: number; limit?: number }): Promise<ApiResponse<any>> =>
    api.get('/api/practice/history', { params }).then(res => res.data),
  
  getProgress: (): Promise<ApiResponse<any>> =>
    api.get('/api/practice/progress').then(res => res.data),
};

// 考试API
export const examsAPI = {
  getExams: (params?: {
    page?: number;
    limit?: number;
    category_id?: number;
    is_published?: boolean;
  }): Promise<ApiResponse<{ items: Exam[]; total: number; page: number; limit: number; totalPages: number }>> =>
    api.get('/api/exams', { params }).then(res => res.data),
  
  getExam: (id: number): Promise<ApiResponse<Exam & { questions: Question[] }>> =>
    api.get(`/api/exams/${id}`).then(res => res.data),
  
  createExam: (data: Omit<Exam, 'id' | 'created_at' | 'updated_at' | 'created_by'> & { question_ids: number[] }): Promise<ApiResponse<{ exam_id: number }>> =>
    api.post('/api/exams', data).then(res => res.data),
  
  startExam: (id: number): Promise<ApiResponse<{
    attempt_id: number;
    exam: Exam;
    questions: Question[];
    started_at: string;
  }>> =>
    api.post(`/api/exams/${id}/start`).then(res => res.data),
  
  submitExamAnswer: (data: { question_id: number; user_answer?: string }): Promise<ApiResponse<{
    is_correct: boolean;
    points_earned: number;
    total_score: number;
  }>> =>
    api.post('/api/exams/submit-answer', data).then(res => res.data),
  
  submitExam: (id: number): Promise<ApiResponse<{
    attempt_id: number;
    score: number;
    max_score: number;
    percentage: number;
    time_spent: number;
    submitted_at: string;
  }>> =>
    api.post(`/api/exams/submit/${id}`).then(res => res.data),
  
  getResults: (id: number): Promise<ApiResponse<any>> =>
    api.get(`/api/exams/results/${id}`).then(res => res.data),
};

// 分析API
export const analyticsAPI = {
  getPersonalStats: (): Promise<ApiResponse<any>> =>
    api.get('/api/analytics/personal').then(res => res.data),
  
  getProgress: (): Promise<ApiResponse<any>> =>
    api.get('/api/analytics/progress').then(res => res.data),
  
  getWeakness: (): Promise<ApiResponse<any>> =>
    api.get('/api/analytics/weakness').then(res => res.data),
  
  getExamHistory: (params?: { page?: number; limit?: number }): Promise<ApiResponse<any>> =>
    api.get('/api/analytics/exam-history', { params }).then(res => res.data),
  
  generateReport: (data: { start_date?: string; end_date?: string; report_type?: string }): Promise<ApiResponse<any>> =>
    api.post('/api/analytics/report', data).then(res => res.data),
};

export default api;
